/**
 * 用户配置管理API接口
 */

import { request } from '@/utils/http'

// API基础路径
const API_BASE = '/sys/user-config'

/**
 * 批量配置请求
 */
export interface BatchConfigRequest {
  /** 配置键列表 */
  configKeys: string[]
  /** 默认值映射（可选） */
  defaultValues?: Record<string, boolean>
}

/**
 * 用户配置保存请求
 */
export interface UserConfigSaveRequest {
  /** 配置键 */
  configKey: string
  /** 配置名称 */
  configName?: string
  /** 配置值（JSON格式） */
  configValue: Record<string, any>
  /** 配置描述 */
  description?: string
}

/**
 * 获取租户布尔配置请求
 */
export interface GetTenantBooleanConfigRequest {
  /** 配置键 */
  configKey: string
  /** 默认值 */
  defaultValue?: boolean
}

/**
 * 保存/更新租户布尔配置请求
 */
export interface SaveOrUpdateTenantBooleanConfigRequest {
  /** 配置键 */
  configKey: string
  /** 是否启用 */
  enabled: boolean
}

/**
 * 刷新租户布尔配置缓存请求
 */
export interface RefreshTenantBooleanConfigRequest {
  /** 配置键 */
  configKey: string
}

/**
 * 获取租户完整配置请求
 */
export interface GetTenantConfigRequest {
  /** 配置键 */
  configKey: string
}

/**
 * 删除租户配置请求
 */
export interface DeleteTenantConfigRequest {
  /** 配置键 */
  configKey: string
}

// ========== 布尔配置接口 ==========

/**
 * 获取租户布尔配置
 */
export const getTenantBooleanConfig = (
  configKey: string,
  defaultValue?: boolean
): Promise<boolean> => {
  return request.post(`${API_BASE}/tenant/boolean/get`, {
    configKey,
    defaultValue
  } as GetTenantBooleanConfigRequest).then(res => res?.content ?? defaultValue ?? false)
}

/**
 * 保存/更新租户布尔配置
 */
export const saveOrUpdateTenantBooleanConfig = (
  configKey: string,
  enabled: boolean
): Promise<boolean> => {
  return request.post(`${API_BASE}/tenant/boolean/save`, {
    configKey,
    enabled
  } as SaveOrUpdateTenantBooleanConfigRequest).then(res => res?.content ?? false)
}

/**
 * 刷新租户布尔配置缓存
 */
export const refreshTenantBooleanConfig = (
  configKey: string
): Promise<boolean> => {
  return request.post(`${API_BASE}/tenant/boolean/refresh`, {
    configKey
  } as RefreshTenantBooleanConfigRequest).then(res => res?.content ?? false)
}

// ========== JSON配置接口 ==========

/**
 * 获取租户完整配置（JSON格式）
 */
/** http 拦截器已解包 content；兼容仍带 content 外壳的响应 */
function unwrapConfigContent(res: unknown): Record<string, any> | null {
  if (res == null) {
    return null
  }
  if (typeof res === 'object' && 'content' in res) {
    const inner = (res as { content?: Record<string, any> | null }).content
    return inner ?? null
  }
  return res as Record<string, any>
}

export const getTenantConfig = (
  configKey: string,
  options?: { showErrorMessage?: boolean }
): Promise<Record<string, any> | null> => {
  return request
    .post(
      `${API_BASE}/tenant/get`,
      { configKey } as GetTenantConfigRequest,
      { showErrorMessage: options?.showErrorMessage ?? false }
    )
    .then(res => unwrapConfigContent(res))
}

/**
 * 保存/更新租户完整配置
 */
export const saveOrUpdateTenantConfig = (
  requestData: UserConfigSaveRequest
): Promise<boolean> => {
  return request.post(`${API_BASE}/tenant/save`, requestData)
    .then(res => res?.content ?? false)
}

/**
 * 删除租户配置
 */
export const deleteTenantConfig = (
  configKey: string
): Promise<boolean> => {
  return request.post(`${API_BASE}/tenant/delete`, {
    configKey
  } as DeleteTenantConfigRequest).then(res => res?.content ?? false)
}

// ========== 批量操作接口 ==========

/**
 * 批量获取租户布尔配置
 */
export const batchGetTenantBooleanConfig = (
  requestData: BatchConfigRequest
): Promise<Record<string, boolean>> => {
  return request.post(`${API_BASE}/tenant/boolean/batch`, requestData)
    .then(res => res?.content ?? {})
}

/**
 * 清空当前租户所有配置缓存
 */
export const clearTenantConfigCache = (): Promise<boolean> => {
  return request.post(`${API_BASE}/tenant/cache/clear`)
    .then(res => res?.content ?? false)
}

// ========== 用户级别配置接口 ==========

/**
 * 获取用户完整配置（JSON格式）
 */
export const getUserConfig = (
  configKey: string
): Promise<Record<string, any> | null> => {
  return request.post(`${API_BASE}/user/get`, {
    configKey
  } as GetTenantConfigRequest).then(res => {
    // 如果 res 直接包含 cards，说明响应结构是 {cards: [...]}
    if (res && 'cards' in res && Array.isArray(res.cards)) {
      return res as Record<string, any>
    }
    // 否则尝试 res.content
    return res?.content ?? null
  })
}

/**
 * 保存/更新用户完整配置
 */
export const saveOrUpdateUserConfig = (
  requestData: UserConfigSaveRequest
): Promise<boolean> => {
  return request.post(`${API_BASE}/user/save`, requestData)
    .then(res => res?.content ?? false)
}

/**
 * 删除用户配置
 */
export const deleteUserConfig = (
  configKey: string
): Promise<boolean> => {
  return request.post(`${API_BASE}/user/delete`, {
    configKey
  } as DeleteTenantConfigRequest).then(res => res?.content ?? false)
}

